import {Badge, Heading} from '@albert-io/atomic';
import type {UserModelV2} from '@albert-io/models';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import {callAction} from 'client/framework';
import type {List, Map} from 'immutable';
import {OrderedMap} from 'immutable';
import {kebabCase, startCase} from 'lodash';
import React, {useEffect} from 'react';

import navigationActions from 'client/GlobalNavigation/GlobalNavigationActions';
import sessionStore from 'client/Session/SessionStore';
import {ADAPTIVE_LABEL} from 'client/generic/SubjectMenuRenderer/constants';
import {
  getActivePathFromPathname,
  getSubgroupItemsAsLinkContents
} from 'client/generic/SubjectMenuRenderer/subjectMenuData.utils';
import {history} from 'client/history';
import {SubjectMenuRenderer} from 'client/generic/SubjectMenuRenderer/SubjectMenuRenderer';
import {getUserQuery} from 'lib/UserAccessUtil';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';

import {withApplicationMetadata} from '../Routing/withApplicationMetadata';

import ClassSubjects from './ClassSubjects/ClassSubjects';
import {useAdaptiveSubjectsQuery} from './SubjectLibrary.queries.generated';
import {ALL_SUBJECTS_STRING} from './subjectLibraryConstants';

const allSubjectsPath = 'subjects';

// `callAction`'s `requestId` is optional at runtime but typed as required; re-type locally.
const dispatchAction = callAction as (name: symbol, payload?: unknown) => unknown;

interface SubjectLibraryProps {
  user: UserModelV2;
  location: {pathname: string};
  route: {content: string};
}

const SubjectLibrary = ({user, location, route}: SubjectLibraryProps) => {
  useEffect(() => {
    dispatchAction(navigationActions.SET_SHOULD_SHOW_UPGRADE_BUTTON_NO_EMIT, true);
    dispatchAction(navigationActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, true);
    return () => {
      dispatchAction(navigationActions.SET_SHOULD_SHOW_UPGRADE_BUTTON, false);
    };
  }, []);

  const {loading: isLoadingAdaptiveSubjectsData, data: adaptiveSubjectsData} =
    useAdaptiveSubjectsQuery();

  const makeSidebarItemHref = (domain, group) => {
    const base = `/${
      domain === ALL_SUBJECTS_STRING ? allSubjectsPath : `subjects/${kebabCase(domain)}`
    }`;
    return group === 'All' ? base : `${base}/${kebabCase(group)}`;
  };

  const makeSubjectCardHref = (subject: {urlSlug: string}) => {
    return `/${subject.urlSlug}`;
  };

  const makeSubjectCardContent = (subject: {description: string}) => {
    return subject.description;
  };

  const onSearchStringUpdate = () => {
    history.pushState(null, `/${allSubjectsPath}`);
  };

  const activePath = getActivePathFromPathname(location.pathname, '/subjects');
  const subgroupMenuContent = getSubgroupItemsAsLinkContents(
    activePath as [string, string],
    '/subjects'
  ).filter((tab?: Map<string, string>) => tab?.get('label') !== 'Free') as List<
    Map<string, string>
  >;

  // This allows us to control the order that the items appear in the sidebar
  let additionalSidebarMenuItems = OrderedMap<string, Record<string, any>>();

  let isUserLicensedTeacher = false;
  if (sessionStore.isTeacher()) {
    additionalSidebarMenuItems = additionalSidebarMenuItems.set('Class Subjects', {
      leftIcon: 'chalkboard'
    });
    const schoolId = user.getTeacher().getSchool().getId();
    const isSchoolLicensed = user.getTeacher().getLicenseStatusForSchool(schoolId);
    // isUserLicensedTeacher refers to teachers being approved at the school AND the school having an active license
    isUserLicensedTeacher = user.getTeacher().getMeta().isLicensed() && isSchoolLicensed;
  }

  const canSeeFreeLicensedBadges =
    sessionStore.hasContentManagerAccess() ||
    (sessionStore.isTeacher() && !isUserLicensedTeacher) ||
    !sessionStore.hasValidSession();

  if (!isLoadingAdaptiveSubjectsData && adaptiveSubjectsData?.subjects?.results?.length) {
    additionalSidebarMenuItems = additionalSidebarMenuItems.set(ADAPTIVE_LABEL, {
      leftIcon: 'sparkles',
      rightContent: canSeeFreeLicensedBadges ? (
        <Badge roundness='square' status='warning' emphasis='default' size='small'>
          Free
        </Badge>
      ) : null
    });
  }

  const isLoggedOutOrStudent = !sessionStore.hasValidSession() || sessionStore.isStudent();

  let subjectAreaContent;
  const contentRoute = route.content;
  if (contentRoute === 'class-subjects') {
    subjectAreaContent = <ClassSubjects isUserLicensedTeacher={isUserLicensedTeacher} />;
  }
  return (
    <div className='course-library'>
      <SubjectMenuRenderer
        className='course-library__subject-menu'
        longDirection='reverse'
        activePath={activePath}
        headerContent={
          <Heading className='pr-x4' as='h1' size='l'>
            Subject Library
          </Heading>
        }
        headerProps={{justifyContent: 'space-between'}}
        makeSidebarItemHref={makeSidebarItemHref}
        subgroupMenuContent={subgroupMenuContent}
        makeSubjectCardContent={makeSubjectCardContent}
        makeSubjectCardHref={makeSubjectCardHref}
        onSearchStringUpdate={onSearchStringUpdate}
        subjectAreaContent={subjectAreaContent}
        additionalSidebarMenuItems={additionalSidebarMenuItems}
        isUserLicensedTeacher={isUserLicensedTeacher}
        isLoggedOutOrStudent={isLoggedOutOrStudent}
        adaptiveSubjects={adaptiveSubjectsData?.subjects?.results ?? []}
        canSeeFreeLicensedBadges={canSeeFreeLicensedBadges}
      />
    </div>
  );
};

export default withApplicationMetadata(
  awaitMandarkQueries(() => {
    const options: {
      queries: {[key: string]: QueryBuilder};
      passthroughProps: {[key: string]: unknown};
    } = {
      queries: {},
      passthroughProps: {}
    };
    if (sessionStore.hasValidSession()) {
      options.queries.user = getUserQuery();
    }
    return options;
  }, SubjectLibrary),
  (props) => {
    const [domain = 'All Subjects', group, subgroup] = getActivePathFromPathname(
      props.location.pathname,
      '/subjects'
    );
    const parts = [startCase(domain), 'Practice', 'Albert'];

    if (group) {
      parts.unshift(group);
    }

    if (subgroup) {
      parts.unshift(subgroup);
    }
    return {
      title: parts.join(' | ')
    };
  }
);
